import { OrgRole, encodeQueryParams } from "@hex/common";
import React from "react";
import { Navigate } from "react-router-dom";

import { useAttribution } from "../hooks/useAttribution";
import { ORG_ID } from "../orgs";
import { useOrgsClientQuery } from "../orgs.generated";
import { OrgAwareRedirect } from "../util/OrgAwareRedirect";

import { useMeQuery } from "./HexRoutes.generated";
import { ClientSideRoute, Routes, useDictQueryParams } from "./routes";

interface AuthenticatedRouteRedirectProps {
  /** The route to redirect to (e.g., Routes.NEW_PROJECT or Routes.NEW_THREAD) */
  toRoute: ClientSideRoute<string, any>;
}

/**
 * Generic redirect component that handles authentication checks and redirects
 * to the appropriate org-specific route.
 * Used for /new-project and /new-thread.
 */
const AuthenticatedRouteRedirect: React.ComponentType<AuthenticatedRouteRedirectProps> =
  React.memo(function AuthenticatedRouteRedirect({ toRoute }) {
    const { data, error: meError, loading: meLoading } = useMeQuery();
    const {
      data: clientOrgs,
      error: clientOrgsError,
      loading: clientOrgsLoading,
    } = useOrgsClientQuery();

    const attribution = useAttribution();
    const queryParams = useDictQueryParams();

    const { signup, ...params } = queryParams;
    const redirectToSignup = signup === "true";

    const isAnonymousUser = !data?.me || data.me.orgRole === OrgRole.ANONYMOUS;

    const isAuthenticated =
      clientOrgs && clientOrgs?.orgsClient.orgs.length > 0;

    const loading =
      meLoading || clientOrgsLoading || !clientOrgs?.orgsClient.loaded;

    if (loading) {
      return null;
    }
    // TODO(VELO-4725): Move this redirect to the server side
    // If user is authenticated into org, redirect to org specific route
    if (!meError && !isAnonymousUser) {
      return (
        <OrgAwareRedirect
          toUrl={toRoute.href(ORG_ID, false, {
            attribution,
            queryParams: params,
          })}
        />
      );
    }
    // If user is authenticated into another org, redirect to workspace picker
    // or first authenticated org.
    if (!clientOrgsError && isAuthenticated) {
      // if authenticated into multiple orgs, redirect to login workspace picker
      if (clientOrgs.orgsClient.orgs.length > 1) {
        return (
          <Navigate
            replace={true}
            to={Routes.WORKSPACE_PICKER.getUrl({
              subView: "login",
              attribution,
              queryParams: {
                redirectTo: `${window.location.pathname}${encodeQueryParams(
                  params,
                )}`,
              },
            })}
          />
        );
      }

      // if authenticated into one org, redirect directly to org
      const orgId = clientOrgs.orgsClient.orgs[0].id;

      return (
        <OrgAwareRedirect
          toUrl={toRoute.href(orgId, false, {
            attribution,
            queryParams: params,
          })}
        />
      );
    }
    // If user is unauthenticated, redirect to signup/login
    return redirectToSignup ? (
      <Navigate
        replace={true}
        to={Routes.SIGNUP.getUrl({
          attribution,
          queryParams: {
            redirectTo: `${window.location.pathname}${encodeQueryParams(
              params,
            )}${window.location.hash}`,
          },
        })}
      />
    ) : (
      <Navigate
        replace={true}
        to={Routes.LOGIN.getUrl({
          attribution,
          queryParams: {
            redirectTo: `${window.location.pathname}${encodeQueryParams(
              params,
            )}${window.location.hash}`,
          },
        })}
      />
    );
  });

export default AuthenticatedRouteRedirect;
